import { useState } from 'react'
import { WifiOffIcon, RefreshCwIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'

/**
 * Offline detection banner.
 * Shows a sticky top banner when the browser goes offline,
 * with a "Retry" button that reloads the page.
 *
 * Usage — mount once in the root layout:
 *   <OfflineBanner />
 */
export function OfflineBanner() {
  const isOnline = useOnlineStatus()
  const [retrying, setRetrying] = useState(false)

  if (isOnline) return null

  const handleRetry = () => {
    setRetrying(true)
    window.location.reload()
  }

  return (
    <div className="sticky top-0 z-[100] flex items-center justify-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm backdrop-blur-sm">
      <WifiOffIcon className="size-4 shrink-0 text-amber-600" />
      <span className="font-medium text-amber-700 dark:text-amber-400">
        You're offline. Some features may be unavailable.
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={handleRetry}
        disabled={retrying}
        className="ml-2 gap-1.5 border-amber-500/30 text-amber-700 hover:bg-amber-500/10 dark:text-amber-400"
      >
        <RefreshCwIcon className={`size-3.5 ${retrying ? 'animate-spin' : ''}`} />
        {retrying ? 'Reconnecting…' : 'Retry'}
      </Button>
    </div>
  )
}
